import { Head, Link, router, useForm } from "@inertiajs/react"
import { DollarSign, Pencil, SquarePen, Trash2 } from "lucide-react"
import type { FormEventHandler } from "react"
import { useState } from "react"

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import AppLayout from "@/layouts/app-layout"
import { formatDate, nowLocalDatetime } from "@/lib/date-utils"
import {
  caseExpensePath,
  createExpenseEquipmentPath,
  editEquipmentPath,
  equipmentIndexPath,
  equipmentLogPath,
  equipmentLogsPath,
  equipmentPath,
  receiptCaseExpensePath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Equipment {
  id: number
  name: string
  display_name: string
  equipment_type: string | null
  year: number | null
  manufacturer: string | null
  model: string | null
  serial_number: string | null
  notes: string | null
  hourly_rate: number | null
  daily_rate: number | null
  requires_odometer: boolean
  requires_location: boolean
  requires_vehicle: boolean
  status: number
  assigned_to: string | null
  assigned: boolean
}

interface Assignment {
  id: number
  assigned_at: string
  returned_at: string | null
  employee_name: string | null
  removed_by_name: string | null
  case_id: number | null
  case_name: string | null
  install_vehicle: string | null
  install_vehicle_location: string | null
  install_gps_location: string | null
  remove_vehicle_location: string | null
  start_odometer: number | null
  end_odometer: number | null
  odometer_usage: number | null
  notes: string | null
  currently_assigned: boolean
}

interface Employee {
  id: number
  name: string
}

interface Vendor {
  id: number
  name: string
}

interface PaymentMethod {
  value: string
  label: string
}

interface Log {
  id: number
  log_date: string
  end_date: string | null
  notes: string | null
  employee_name: string | null
  vendor_id: number | null
  vendor_name: string | null
  start_odometer: number | null
  end_odometer: number | null
  odometer_usage: number | null
  can_edit: boolean
  can_delete: boolean
}

interface Expense {
  id: number
  case_id: number | null
  expense_date: string
  payee: string | null
  notes: string | null
  amount: number | null
  paid_by: string | null
  approved: boolean
  has_receipt: boolean
  employee_name: string | null
}

interface Props {
  equipment: Equipment
  assignments: Assignment[]
  logs: Log[]
  expenses: Expense[]
  can_assign: boolean
  can_edit: boolean
  can_deactivate: boolean
  can_manage_logs: boolean
  can_add_log: boolean
  employees: Employee[]
  vendor_options: Vendor[]
  equipment_vendor_options: Vendor[]
  payment_methods: PaymentMethod[]
}

export default function Show({
  equipment,
  assignments,
  logs,
  expenses,
  can_assign,
  can_edit,
  can_deactivate,
  can_add_log,
  employees,
  vendor_options,
  equipment_vendor_options,
  payment_methods,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Equipment", href: equipmentIndexPath() },
    { title: equipment.display_name, href: equipmentPath(equipment.id) },
  ]

  const isActive = equipment.status === 0
  const isInactive = equipment.status === 1

  const [showExpenseModal, setShowExpenseModal] = useState(false)
  const [editingLog, setEditingLog] = useState<Log | "new" | null>(null)
  const [assigneeId, setAssigneeId] = useState<number | "">("")
  const [showAssignModal, setShowAssignModal] = useState(false)

  const handleDeactivate = () => {
    if (confirm("Are you sure you want to deactivate this equipment?")) {
      router.patch(`/equipment/${equipment.id}/deactivate`)
    }
  }

  const handleReactivate = () => {
    router.patch(`/equipment/${equipment.id}/reactivate`)
  }

  const handleAssignClick = () => {
    if (equipment.requires_location) {
      setShowAssignModal(true)
    } else {
      handleQuickAssign()
    }
  }

  const handleQuickAssign = () => {
    if (!assigneeId) return
    router.post(`/equipment/${equipment.id}/assign`, {
      equipment_assignment: {
        user_id: assigneeId,
        assigned_at: new Date().toISOString(),
      },
    })
  }

  const formatCurrency = (value: number | null) => {
    if (value == null) return null
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value)
  }

  const formatDateTime = (value: string | null) => {
    if (!value) return "-"
    return formatDate(value)
  }

  const makeModel = [equipment.manufacturer, equipment.model]
    .filter(Boolean)
    .join(" ")

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={equipment.display_name} />

      <div className="mx-auto max-w-5xl p-6">
        {/* ── Header ────────────────────────────── */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-hpi-dark-blue text-2xl font-semibold">
                {equipment.display_name}
              </h1>
              {can_edit && (
                <Link
                  href={editEquipmentPath(equipment.id)}
                  className="text-hpi-med-blue hover:text-hpi-active-blue"
                  title="Edit equipment"
                >
                  <SquarePen className="h-4 w-4" />
                </Link>
              )}
              {isInactive && (
                <span className="bg-hpi-light-red text-hpi-red rounded-full px-2.5 py-0.5 text-xs font-medium">
                  Inactive
                </span>
              )}
            </div>
            <div className="mt-1.5 flex gap-2 text-xs">
              <a
                href="#assignments"
                className="bg-hpi-med-blue hover:bg-hpi-active-blue rounded px-2 py-0.5 font-medium text-white transition-colors"
              >
                Assignments
              </a>
              <a
                href="#logs"
                className="bg-hpi-med-blue hover:bg-hpi-active-blue rounded px-2 py-0.5 font-medium text-white transition-colors"
              >
                Logs
              </a>
              <a
                href="#expenses"
                className="bg-hpi-med-blue hover:bg-hpi-active-blue rounded px-2 py-0.5 font-medium text-white transition-colors"
              >
                Expenses
              </a>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {can_deactivate && isActive && (
              <button
                onClick={handleDeactivate}
                className="border-hpi-med-gray text-hpi-dark-gray hover:bg-hpi-pale-gray rounded border px-3 py-1.5 text-sm"
              >
                Deactivate
              </button>
            )}
            {can_deactivate && isInactive && (
              <button
                onClick={handleReactivate}
                className="border-hpi-dark-green text-hpi-dark-green hover:bg-hpi-light-green rounded border px-3 py-1.5 text-sm"
              >
                Reactivate
              </button>
            )}
          </div>
        </div>

        {/* ── Equipment Info Card ────────────────── */}
        <div className="bg-hpi-pale-gray mb-6 rounded-lg p-5">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-3">
            <InfoItem label="Type" value={equipment.equipment_type} />
            <InfoItem label="Make / Model" value={makeModel || null} />
            <InfoItem label="Year" value={equipment.year?.toString()} />
            <InfoItem label="Serial #" value={equipment.serial_number} />
            {equipment.hourly_rate != null && (
              <InfoItem
                label="Hourly Rate"
                value={formatCurrency(equipment.hourly_rate)}
              />
            )}
            {equipment.daily_rate != null && (
              <InfoItem
                label="Daily Rate"
                value={formatCurrency(equipment.daily_rate)}
              />
            )}
            {equipment.requires_odometer && (
              <InfoItem label="Odometer/Hours" value="Required" />
            )}
            <InfoItem
              label="Status"
              value={
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${equipment.assigned ? "bg-hpi-active-blue" : "bg-hpi-med-gray"}`}
                  />
                  {equipment.assigned
                    ? `Assigned to ${equipment.assigned_to}`
                    : "Unassigned"}
                </span>
              }
            />
          </div>
          {equipment.notes && (
            <div className="border-hpi-light-gray mt-4 border-t pt-3">
              <span className="text-hpi-med-blue text-xs font-medium tracking-wide uppercase">
                Notes
              </span>
              <p className="text-hpi-dark-gray mt-1 text-sm whitespace-pre-wrap">
                {equipment.notes}
              </p>
            </div>
          )}
        </div>

        {/* ── Assignment History ─────────────────── */}
        <div id="assignments">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-hpi-dark-blue text-lg font-semibold">
              Assignment History
            </h2>

            {can_assign && (
              <div className="flex items-center gap-2">
                <select
                  value={assigneeId}
                  onChange={(e) =>
                    setAssigneeId(
                      e.target.value ? parseInt(e.target.value) : "",
                    )
                  }
                  className="border-hpi-light-gray rounded border px-2 py-1.5 text-sm"
                >
                  <option value="">Select employee...</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAssignClick}
                  disabled={!assigneeId}
                  className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-3 py-1.5 text-sm text-white disabled:opacity-40"
                >
                  {equipment.assigned ? "Re-assign" : "Assign"}
                </button>
              </div>
            )}
          </div>

          {assignments.length > 0 ? (
            <ul className="space-y-3">
              {assignments.map((a) => {
                const hasOdometer =
                  equipment.requires_odometer ||
                  (a.start_odometer != null && a.start_odometer > 0) ||
                  (a.end_odometer != null && a.end_odometer > 0)
                const hasInstallDetails =
                  a.install_vehicle ||
                  a.install_vehicle_location ||
                  a.install_gps_location
                const hasReturnDetails =
                  a.remove_vehicle_location || a.removed_by_name

                return (
                  <li
                    key={a.id}
                    className={`border-hpi-light-gray rounded border p-4 ${a.currently_assigned ? "border-hpi-active-blue/40 bg-hpi-light-blue/10" : ""}`}
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-hpi-dark-blue font-medium">
                          {a.employee_name || "Unknown"}
                        </span>
                        {a.currently_assigned && (
                          <span className="bg-hpi-dark-green rounded px-2 py-0.5 text-xs text-white">
                            Current
                          </span>
                        )}
                        {a.case_name && (
                          <span className="text-hpi-med-blue text-xs">
                            {a.case_name}
                          </span>
                        )}
                      </div>
                      <div className="text-hpi-dark-gray text-xs">
                        {formatDateTime(a.assigned_at)}
                        {!a.currently_assigned && a.returned_at && (
                          <> &ndash; {formatDateTime(a.returned_at)}</>
                        )}
                      </div>
                    </div>

                    <div className="text-hpi-dark-gray mt-1 space-y-1 text-sm">
                      {hasInstallDetails && (
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                          {a.install_vehicle && (
                            <span>
                              <span className="text-hpi-med-blue text-xs font-medium">
                                Vehicle:
                              </span>{" "}
                              {a.install_vehicle}
                            </span>
                          )}
                          {a.install_vehicle_location && (
                            <span>
                              <span className="text-hpi-med-blue text-xs font-medium">
                                Install Location:
                              </span>{" "}
                              {a.install_vehicle_location}
                            </span>
                          )}
                          {a.install_gps_location && (
                            <span>
                              <span className="text-hpi-med-blue text-xs font-medium">
                                GPS Location:
                              </span>{" "}
                              {a.install_gps_location}
                            </span>
                          )}
                        </div>
                      )}

                      {hasReturnDetails && !a.currently_assigned && (
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                          {a.removed_by_name && (
                            <span>
                              <span className="text-hpi-med-blue text-xs font-medium">
                                Returned by:
                              </span>{" "}
                              {a.removed_by_name}
                            </span>
                          )}
                          {a.remove_vehicle_location && (
                            <span>
                              <span className="text-hpi-med-blue text-xs font-medium">
                                Remove Location:
                              </span>{" "}
                              {a.remove_vehicle_location}
                            </span>
                          )}
                        </div>
                      )}

                      {hasOdometer && (
                        <div>
                          <span className="text-hpi-med-blue text-xs font-medium">
                            Odo/Hours:
                          </span>{" "}
                          {a.start_odometer ?? "?"} &ndash;{" "}
                          {a.end_odometer ?? "?"}
                          {a.odometer_usage != null && (
                            <span className="text-hpi-med-blue ml-1">
                              ({a.odometer_usage})
                            </span>
                          )}
                        </div>
                      )}

                      {a.notes && (
                        <div className="text-hpi-med-gray">{a.notes}</div>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className="text-hpi-med-gray py-4 text-sm">
              No assignment history.
            </p>
          )}
        </div>

        {/* ── Logs ─────────────────────────────────── */}
        <div id="logs" className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-hpi-dark-blue text-lg font-semibold">Logs</h2>
            {can_add_log && (
              <button
                type="button"
                onClick={() => setEditingLog("new")}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-3 py-1.5 text-sm text-white"
              >
                Add Log
              </button>
            )}
          </div>
          {logs.length > 0 ? (
            <ul className="space-y-3">
              {logs.map((log) => (
                <li
                  key={log.id}
                  className="border-hpi-light-gray rounded border bg-white px-4 py-3"
                >
                  {/* Row 1: date + employee (primary), actions (right) */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-2">
                      <span className="text-hpi-dark-blue text-sm font-semibold">
                        {formatDateTime(log.log_date)}
                        {log.end_date && (
                          <> to {formatDateTime(log.end_date)}</>
                        )}
                      </span>
                      {log.employee_name && (
                        <span className="text-hpi-med-blue text-xs">
                          {log.employee_name}
                        </span>
                      )}
                    </div>
                    {(log.can_edit || log.can_delete) && (
                      <div className="flex gap-1.5">
                        {log.can_edit && (
                          <button
                            type="button"
                            onClick={() => setEditingLog(log)}
                            className="text-hpi-med-blue hover:text-hpi-active-blue"
                            title="Edit log"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {log.can_delete && (
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                confirm(
                                  "Are you sure you want to delete this log?",
                                )
                              ) {
                                router.delete(equipmentLogPath(log.id), {
                                  data: { redirect_to: "equipment" },
                                  preserveScroll: true,
                                })
                              }
                            }}
                            className="text-hpi-red hover:text-hpi-red/70"
                            title="Delete log"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Row 2: metadata chips (secondary) */}
                  {(log.vendor_name ||
                    (log.start_odometer != null && log.start_odometer > 0)) && (
                    <div className="text-hpi-dark-gray mt-1 flex flex-wrap gap-x-4 text-xs">
                      {log.vendor_name && (
                        <span>
                          <span className="text-hpi-med-blue font-medium">
                            Vendor:
                          </span>{" "}
                          {log.vendor_name}
                        </span>
                      )}
                      {log.start_odometer != null && log.start_odometer > 0 && (
                        <span>
                          <span className="text-hpi-med-blue font-medium">
                            Odo/Hours:
                          </span>{" "}
                          {log.start_odometer}
                          {log.end_odometer != null && log.end_odometer > 0 && (
                            <>
                              {" "}
                              to {log.end_odometer} ({log.odometer_usage})
                            </>
                          )}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Row 3: notes (tertiary) */}
                  {log.notes && (
                    <p className="text-hpi-dark-gray mt-1.5 text-sm leading-relaxed whitespace-pre-wrap">
                      {log.notes}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-hpi-med-gray py-4 text-sm">
              No logs for this equipment.
            </p>
          )}
        </div>

        {/* ── Expenses ─────────────────────────────── */}
        <div id="expenses" className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-hpi-dark-blue text-lg font-semibold">
              Expenses
            </h2>
            {can_add_log && (
              <button
                type="button"
                onClick={() => setShowExpenseModal(true)}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-3 py-1.5 text-sm text-white"
              >
                Add Expense
              </button>
            )}
          </div>
          {expenses.length > 0 ? (
            <>
              <table className="border-hpi-light-gray w-full border-collapse border text-sm">
                <thead>
                  <tr className="bg-hpi-pale-gray">
                    <th className="border-hpi-light-gray border px-3 py-2 text-left font-medium">
                      Date
                    </th>
                    <th className="border-hpi-light-gray border px-3 py-2 text-left font-medium">
                      Payee
                    </th>
                    <th className="border-hpi-light-gray border px-3 py-2 text-left font-medium">
                      Description
                    </th>
                    <th className="border-hpi-light-gray border px-3 py-2 text-right font-medium">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((exp) => (
                    <tr
                      key={exp.id}
                      className="hover:bg-hpi-pale-blue bg-white"
                    >
                      <td className="border-hpi-light-gray border px-3 py-2 whitespace-nowrap">
                        {exp.case_id ? (
                          <Link
                            href={caseExpensePath(exp.case_id, exp.id)}
                            className="text-hpi-active-blue hover:underline"
                          >
                            {formatDateTime(exp.expense_date)}
                          </Link>
                        ) : (
                          formatDateTime(exp.expense_date)
                        )}
                      </td>
                      <td className="border-hpi-light-gray border px-3 py-2">
                        {exp.payee || "-"}
                      </td>
                      <td className="border-hpi-light-gray border px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="line-clamp-2">
                            {exp.notes || "-"}
                          </span>
                          {exp.has_receipt && exp.case_id && (
                            <a
                              href={receiptCaseExpensePath(exp.case_id, exp.id)}
                              target="_blank"
                              rel="noreferrer"
                              className="text-hpi-med-blue hover:text-hpi-active-blue shrink-0"
                              title="View receipt"
                            >
                              <DollarSign className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="border-hpi-light-gray border px-3 py-2 text-right whitespace-nowrap">
                        {formatCurrency(exp.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-hpi-pale-gray font-semibold">
                    <td
                      colSpan={3}
                      className="border-hpi-light-gray border px-3 py-2 text-right"
                    >
                      Total
                    </td>
                    <td className="border-hpi-light-gray border px-3 py-2 text-right">
                      {formatCurrency(
                        expenses.reduce((sum, e) => sum + (e.amount || 0), 0),
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </>
          ) : (
            <p className="text-hpi-med-gray py-4 text-sm">
              No expenses for this equipment.
            </p>
          )}
        </div>
      </div>

      {/* ── Assign Modal (location-requiring types) ── */}
      {equipment.requires_location && (
        <AssignModal
          open={showAssignModal}
          onOpenChange={setShowAssignModal}
          equipmentId={equipment.id}
          assigneeId={assigneeId}
          employees={employees}
          requiresVehicle={equipment.requires_vehicle}
          onAssigneeChange={setAssigneeId}
        />
      )}

      {/* ── Add Expense Modal ───────────────────── */}
      <AddExpenseModal
        open={showExpenseModal}
        onOpenChange={setShowExpenseModal}
        equipmentId={equipment.id}
        requiresOdometer={equipment.requires_odometer}
        vendorOptions={vendor_options}
        paymentMethods={payment_methods}
      />

      {/* ── Log Modal (add/edit) ─────────────────── */}
      <LogModal
        log={editingLog}
        onClose={() => setEditingLog(null)}
        equipmentId={equipment.id}
        requiresOdometer={equipment.requires_odometer}
        vendorOptions={equipment_vendor_options}
      />
    </AppLayout>
  )
}

function InfoItem({
  label,
  value,
}: {
  label: string
  value: string | React.ReactNode | null | undefined
}) {
  if (value == null || value === "") return null
  return (
    <div>
      <span className="text-hpi-med-blue text-xs font-medium tracking-wide uppercase">
        {label}
      </span>
      <div className="text-hpi-dark-gray mt-0.5 text-sm">{value}</div>
    </div>
  )
}

/* ── Assign Modal (location-requiring equipment) ──── */

function AssignModal({
  open,
  onOpenChange,
  equipmentId,
  assigneeId,
  employees,
  requiresVehicle,
  onAssigneeChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  equipmentId: number
  assigneeId: number | ""
  employees: Employee[]
  requiresVehicle: boolean
  onAssigneeChange: (id: number | "") => void
}) {
  const [assignedAt, setAssignedAt] = useState(nowLocalDatetime)
  const [installVehicle, setInstallVehicle] = useState("")
  const [installVehicleLocation, setInstallVehicleLocation] = useState("")
  const [installGpsLocation, setInstallGpsLocation] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const resetFields = () => {
    setAssignedAt(nowLocalDatetime())
    setInstallVehicle("")
    setInstallVehicleLocation("")
    setInstallGpsLocation("")
  }

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault()
    if (!assigneeId) return
    setSubmitting(true)
    router.post(
      `/equipment/${equipmentId}/assign`,
      {
        equipment_assignment: {
          user_id: assigneeId,
          assigned_at: assignedAt,
          install_vehicle: installVehicle || undefined,
          install_vehicle_location: installVehicleLocation || undefined,
          install_gps_location: installGpsLocation || undefined,
        },
      },
      {
        onSuccess: () => {
          resetFields()
          onOpenChange(false)
        },
        onFinish: () => setSubmitting(false),
        preserveScroll: true,
      },
    )
  }

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue text-sm"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) resetFields()
        onOpenChange(isOpen)
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign Equipment</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>Employee</label>
            <select
              className={inputClass}
              value={assigneeId}
              onChange={(e) =>
                onAssigneeChange(e.target.value ? parseInt(e.target.value) : "")
              }
              required
            >
              <option value="">Select employee...</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Date & Time</label>
            <input
              type="datetime-local"
              className={inputClass}
              value={assignedAt}
              onChange={(e) => setAssignedAt(e.target.value)}
              required
            />
          </div>

          {requiresVehicle && (
            <>
              <div>
                <label className={labelClass}>Vehicle</label>
                <input
                  type="text"
                  className={inputClass}
                  value={installVehicle}
                  onChange={(e) => setInstallVehicle(e.target.value)}
                  placeholder="Tag/state, make, model, color"
                />
              </div>
              <div>
                <label className={labelClass}>
                  Location of Vehicle When Device Installed
                </label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={installVehicleLocation}
                  onChange={(e) => setInstallVehicleLocation(e.target.value)}
                  placeholder="Address or description of location"
                />
              </div>
            </>
          )}

          <div>
            <label className={labelClass}>Device Placement</label>
            <textarea
              className={inputClass}
              rows={2}
              value={installGpsLocation}
              onChange={(e) => setInstallGpsLocation(e.target.value)}
              placeholder="Where device is placed (leave blank if just handing off)"
            />
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!assigneeId || submitting}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {submitting ? "Assigning..." : "Assign"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* ── Expense Modal ────────────────────────────────── */

function AddExpenseModal({
  open,
  onOpenChange,
  equipmentId,
  requiresOdometer,
  vendorOptions,
  paymentMethods,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  equipmentId: number
  requiresOdometer: boolean
  vendorOptions: Vendor[]
  paymentMethods: PaymentMethod[]
}) {
  const { data, setData, processing, errors, reset } = useForm({
    equipment_id: equipmentId,
    log_date: new Date().toISOString().split("T")[0],
    paid_by: "",
    amount: "",
    vendor_id: "",
    paid_to: "",
    notes: "",
    start_odometer: "",
  })

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault()
    router.post(
      createExpenseEquipmentPath(equipmentId),
      {
        case_expense: {
          expense_date: data.log_date,
          paid_by: data.paid_by,
          amount: data.amount,
          vendor_id: data.vendor_id || null,
          paid_to: data.vendor_id ? "" : data.paid_to,
          notes: data.notes,
          fuel_start_odometer: data.start_odometer || null,
        },
      },
      {
        onSuccess: () => {
          reset()
          onOpenChange(false)
        },
        preserveScroll: true,
      },
    )
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-2.5 py-1.5 text-sm focus:border-hpi-active-blue focus:ring-1 focus:ring-hpi-active-blue focus:outline-none"
  const labelClass = "block text-xs font-medium text-hpi-dark-gray"
  const errorClass = "mt-0.5 text-xs text-hpi-red"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Equipment Expense</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Date</label>
              <input
                type="date"
                className={inputClass}
                value={data.log_date}
                onChange={(e) => setData("log_date", e.target.value)}
                required
              />
              {errors.log_date && (
                <p className={errorClass}>{errors.log_date}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Payment Method</label>
              <select
                className={inputClass}
                value={data.paid_by}
                onChange={(e) => setData("paid_by", e.target.value)}
                required
              >
                <option value="">Select...</option>
                {paymentMethods.map((pm) => (
                  <option key={pm.value} value={pm.value}>
                    {pm.label}
                  </option>
                ))}
              </select>
              {errors.paid_by && <p className={errorClass}>{errors.paid_by}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Amount</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                className={inputClass}
                value={data.amount}
                onChange={(e) => setData("amount", e.target.value)}
                required
              />
              {errors.amount && <p className={errorClass}>{errors.amount}</p>}
            </div>
            <div>
              <label className={labelClass}>Vendor</label>
              <select
                className={inputClass}
                value={data.vendor_id}
                onChange={(e) => setData("vendor_id", e.target.value)}
              >
                <option value="">None</option>
                {vendorOptions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!data.vendor_id && (
            <div>
              <label className={labelClass}>Paid To</label>
              <input
                type="text"
                className={inputClass}
                value={data.paid_to}
                onChange={(e) => setData("paid_to", e.target.value)}
                placeholder="Who was paid?"
              />
              {errors.paid_to && <p className={errorClass}>{errors.paid_to}</p>}
            </div>
          )}

          {requiresOdometer && (
            <div>
              <label className={labelClass}>Odometer / Hours</label>
              <input
                type="number"
                step="0.01"
                className={inputClass}
                value={data.start_odometer}
                onChange={(e) => setData("start_odometer", e.target.value)}
              />
            </div>
          )}

          <div>
            <label className={labelClass}>Explain Expense</label>
            <textarea
              className={inputClass}
              rows={3}
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              required
            />
            {errors.notes && <p className={errorClass}>{errors.notes}</p>}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-gray rounded border px-4 py-1.5 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-1.5 text-sm text-white disabled:opacity-50"
            >
              {processing ? "Saving..." : "Save Expense"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* ── Log Modal (add / edit) ───────────────────────── */

function LogModal({
  log,
  onClose,
  equipmentId,
  requiresOdometer,
  vendorOptions,
}: {
  log: Log | "new" | null
  onClose: () => void
  equipmentId: number
  requiresOdometer: boolean
  vendorOptions: Vendor[]
}) {
  const isNew = log === "new"
  const editing = log && log !== "new" ? log : null

  const { data, setData, processing, errors, reset } = useForm({
    log_date: "",
    vendor_id: "",
    start_odometer: "",
    notes: "",
  })

  const [initialized, setInitialized] = useState<number | "new" | null>(null)

  const logKey = isNew ? "new" : (editing?.id ?? null)
  if (log && logKey !== initialized) {
    if (isNew) {
      reset()
      setData("log_date", new Date().toISOString().split("T")[0])
    } else if (editing) {
      setData({
        log_date: editing.log_date ? editing.log_date.split("T")[0] : "",
        vendor_id: editing.vendor_id ? String(editing.vendor_id) : "",
        start_odometer:
          editing.start_odometer && editing.start_odometer > 0
            ? String(editing.start_odometer)
            : "",
        notes: editing.notes || "",
      })
    }
    setInitialized(logKey)
  }

  const closeModal = () => {
    reset()
    setInitialized(null)
    onClose()
  }

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault()
    const options = { onSuccess: closeModal, preserveScroll: true }

    if (isNew) {
      router.post(
        equipmentLogsPath(),
        {
          redirect_to: "equipment",
          equipment_log: {
            equipment_id: equipmentId,
            log_date: data.log_date,
            vendor_id: data.vendor_id || null,
            start_odometer: data.start_odometer || null,
            notes: data.notes,
          },
        },
        options,
      )
    } else if (editing) {
      router.patch(
        equipmentLogPath(editing.id),
        {
          redirect_to: "equipment",
          equipment_log: {
            equipment_id: equipmentId,
            log_date: data.log_date,
            vendor_id: data.vendor_id || null,
            start_odometer: data.start_odometer || null,
            notes: data.notes,
          },
        },
        options,
      )
    }
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-2.5 py-1.5 text-sm focus:border-hpi-active-blue focus:ring-1 focus:ring-hpi-active-blue focus:outline-none"
  const labelClass = "block text-xs font-medium text-hpi-dark-gray"
  const errorClass = "mt-0.5 text-xs text-hpi-red"

  return (
    <Dialog
      open={!!log}
      onOpenChange={(open) => {
        if (!open) closeModal()
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isNew ? "Add Log Entry" : "Edit Log Entry"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>Date</label>
            <input
              type="date"
              className={inputClass}
              value={data.log_date}
              onChange={(e) => setData("log_date", e.target.value)}
              required
            />
            {errors.log_date && <p className={errorClass}>{errors.log_date}</p>}
          </div>

          <div>
            <label className={labelClass}>Vendor (if applicable)</label>
            <select
              className={inputClass}
              value={data.vendor_id}
              onChange={(e) => setData("vendor_id", e.target.value)}
            >
              <option value="">N/A</option>
              {vendorOptions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>

          {requiresOdometer && (
            <div>
              <label className={labelClass}>Odo/Hours (opt)</label>
              <input
                type="number"
                step="0.01"
                className={inputClass}
                value={data.start_odometer}
                onChange={(e) => setData("start_odometer", e.target.value)}
              />
            </div>
          )}

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              className={inputClass}
              rows={6}
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              required
            />
            {errors.notes && <p className={errorClass}>{errors.notes}</p>}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={closeModal}
              className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-gray rounded border px-4 py-1.5 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-1.5 text-sm text-white disabled:opacity-50"
            >
              {processing ? "Saving..." : isNew ? "Add" : "Save"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
